import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import authService from "../../services/authService";

const ADMIN_NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard" },
  { to: "/admin/users", label: "User Management" },
  { to: "/admin/data-view", label: "Data View" },
  { to: "/admin/pubmed-ingest", label: "PubMed Ingest" },
  { to: "/admin/field-access", label: "Field Access" },
];

const AdminHeader = ({ title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userInitials, setUserInitials] = useState("AD");
  const dropdownRef = useRef(null);

  const visibleNavItems = ADMIN_NAV_ITEMS.filter((item) => item.to !== location.pathname);
  const pubMedIngestNavItem = ADMIN_NAV_ITEMS.find(
    (item) => item.to === "/admin/pubmed-ingest"
  );
  const fieldAccessNavItem = ADMIN_NAV_ITEMS.find(
    (item) => item.to === "/admin/field-access"
  );
  const shouldShowPubMedIngestDropdownButton =
    pubMedIngestNavItem && location.pathname !== pubMedIngestNavItem.to;
  const shouldShowFieldAccessDropdownButton =
    fieldAccessNavItem && location.pathname !== fieldAccessNavItem.to;
  const headerNavItems = visibleNavItems.filter(
    (item) =>
      item.to !== pubMedIngestNavItem?.to && item.to !== fieldAccessNavItem?.to
  );
  const dropdownNavItems = visibleNavItems.filter(
    (item) =>
      item.to !== pubMedIngestNavItem?.to && item.to !== fieldAccessNavItem?.to
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setUserInitials("AD");
      return;
    }

    try {
      const userData = JSON.parse(storedUser);
      const firstInitial = userData.first_name
        ? userData.first_name.charAt(0).toUpperCase()
        : "";
      const lastInitial = userData.last_name
        ? userData.last_name.charAt(0).toUpperCase()
        : "";
      const fallbackInitials = userData.email
        ? userData.email.slice(0, 2).toUpperCase()
        : "AD";
      setUserInitials(firstInitial + lastInitial || fallbackInitials);
    } catch (error) {
      setUserInitials("AD");
    }
  }, []);

  const handleMenuClose = () => {
    setOpenMenu(false);
  };

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    setOpenMenu(false);

    authService.logout().catch((error) => {
      console.error("Logout error:", error);
    });

    navigate("/admin/login", { replace: true });
  };


  return (
    <header className="sticky top-0 z-50 border-b border-slate-700/20 bg-[#111318]/95 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-10">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <Link
            to="/admin/dashboard"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 to-blue-700"
          >
            <span className="sr-only">Admin Home</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="white"
                strokeWidth="1.5"
                fill="none"
              />
              <circle
                cx="12"
                cy="12"
                r="4"
                stroke="white"
                strokeWidth="1.5"
                fill="none"
              />
              <circle cx="12" cy="12" r="1.5" fill="white" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1">
            <Link
              to="/admin/dashboard"
              className="block break-words text-[15px] font-semibold leading-tight tracking-[-0.01em] text-slate-50 sm:text-[17px]"
            >
              {title}
            </Link>
            <div className="break-words pr-1 text-[11px] font-medium leading-tight text-slate-500">
              {subtitle}
            </div>
          </div>
        </div>

        <div className="ml-3 flex shrink-0 items-center justify-end gap-3 sm:gap-5">
          <div className="hidden flex-wrap items-center justify-end gap-3 sm:flex sm:gap-4">
            {headerNavItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-slate-700 text-[13px] font-semibold text-slate-400"
              onClick={() => setOpenMenu((prev) => !prev)}
            >
              {userInitials}
            </button>

            {openMenu && (
              <div className="absolute right-0 top-11 z-[60] w-[min(18rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-[0_8px_20px_rgba(0,0,0,0.35)] sm:w-56">
                <ul className="py-2">
                  {dropdownNavItems.map((item) => (
                    <li key={item.to} className="sm:hidden">
                      <Link
                        to={item.to}
                        onClick={handleMenuClose}
                        className="block px-3.5 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                  {visibleNavItems.length > 0 && (
                    <li className="mx-3.5 my-2 border-t border-slate-800 sm:hidden" />
                  )}
                  {shouldShowPubMedIngestDropdownButton ? (
                    <li>
                      <Link
                        to={pubMedIngestNavItem.to}
                        onClick={handleMenuClose}
                        className="block px-3.5 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800 hover:text-slate-50"
                      >
                        {pubMedIngestNavItem.label}
                      </Link>
                    </li>
                  ) : null}
                  {shouldShowFieldAccessDropdownButton ? (
                    <li>
                      <Link
                        to={fieldAccessNavItem.to}
                        onClick={handleMenuClose}
                        className="block px-3.5 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800 hover:text-slate-50"
                      >
                        {fieldAccessNavItem.label}
                      </Link>
                    </li>
                  ) : null}
                  <li>
                    <button
                      type="button"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="block w-full cursor-pointer px-3.5 py-2.5 text-left text-sm text-slate-200 transition-colors  hover:bg-red-500/10 hover:text-red-400"
                    >
                      {isLoggingOut ? "Logging out..." : "Logout"}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
